import { useState, useEffect } from 'react';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc } from 'firebase/firestore';
import { db } from '../firebaseConfig';

export interface Seller {
  id?: string;
  name: string;
  account: string;
  ranges: Array<{
    from: string;
    to: string;
  }>;
  commission: number;
  active: boolean;
}

export function useSellers() {
  const [sellers, setSellers] = useState<Seller[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchSellers = async () => {
    try {
      setLoading(true);
      const querySnapshot = await getDocs(collection(db, 'sellers'));
      const sellersData: Seller[] = [];
      querySnapshot.forEach((doc) => {
        sellersData.push({ id: doc.id, ...doc.data() } as Seller);
      });
      setSellers(sellersData);
      setError(null);
    } catch (err) {
      console.error('Error fetching sellers:', err);
      setError('Error al cargar vendedores');
    } finally {
      setLoading(false);
    }
  };

  const addSeller = async (seller: Omit<Seller, 'id'>) => {
    try {
      const docRef = await addDoc(collection(db, 'sellers'), seller);
      const newSeller = { id: docRef.id, ...seller };
      setSellers(prev => [...prev, newSeller]);
      return docRef.id;
    } catch (err) {
      console.error('Error adding seller:', err);
      setError('Error al agregar vendedor');
      throw err;
    }
  };

  const updateSeller = async (id: string, updates: Partial<Seller>) => {
    try {
      await updateDoc(doc(db, 'sellers', id), updates);
      setSellers(prev => prev.map(seller => 
        seller.id === id ? { ...seller, ...updates } : seller
      ));
    } catch (err) {
      console.error('Error updating seller:', err);
      setError('Error al actualizar vendedor');
      throw err;
    }
  };

  const deleteSeller = async (id: string) => {
    try {
      await deleteDoc(doc(db, 'sellers', id));
      setSellers(prev => prev.filter(seller => seller.id !== id));
    } catch (err) {
      console.error('Error deleting seller:', err);
      setError('Error al eliminar vendedor');
      throw err;
    }
  };

  useEffect(() => {
    fetchSellers();
  }, []);

  return {
    sellers,
    loading,
    error,
    fetchSellers,
    addSeller,
    updateSeller,
    deleteSeller
  };
} 